import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useCourseImport } from '@/hooks/useCourseImport';
import { searchCoursesByName, getCourseById } from '@/integrations/supabase/courseService';
import { addCourseToUser } from '@/integrations/supabase/profileCourseService';
import { toast } from '@/hooks/use-toast';
import CourseSearchStep from './CourseSearchStep';
import CoursePreviewStep from './CoursePreviewStep';
import TeeMappingStep from './TeeMappingStep';
import type { Course } from '@/types';

const STEP_TITLES = {
  preview: 'Course Preview',
  mapping: 'Map Tee Colors',
  importing: 'Importing...',
  done: 'Import Complete',
} as const;

interface CourseSearchInlineProps {
  onImported: () => void;
  onSelectCourse?: (course: Course) => void;
}

interface LocalCourseResult {
  id: string;
  name: string;
  city: string;
  state: string;
  api_course_id: number | null;
}

const CourseSearchInline = ({ onImported, onSelectCourse }: CourseSearchInlineProps) => {
  const { user } = useAuth();
  const {
    step,
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    selectSearchResult,
    selectedCourse,
    loadingDetails,
    proceedToMapping,
    teeMappings,
    updateTeeMapping,
    doImport,
    reset,
    goBack,
    importedCourseId,
  } = useCourseImport();

  const [localResults, setLocalResults] = useState<LocalCourseResult[]>([]);

  // Debounced local DB search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setLocalResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      const results = await searchCoursesByName(searchQuery.trim());
      setLocalResults(results);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle step='done' from dedup or import
  useEffect(() => {
    if (step !== 'done') return;

    const handleDone = async () => {
      onImported();

      if (onSelectCourse && importedCourseId) {
        const course = await getCourseById(importedCourseId);
        if (course) {
          onSelectCourse(course);
        }
      }

      setTimeout(reset, 300);
    };

    handleDone();
  }, [step, onImported, onSelectCourse, importedCourseId, reset]);

  const dialogOpen = step === 'preview' || step === 'mapping' || step === 'importing';

  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen) reset();
  };

  const handleImport = async () => {
    await doImport();
    // useEffect on step='done' handles the rest
  };

  const handleLocalSelect = async (course: LocalCourseResult) => {
    if (!user?.id) return;
    await addCourseToUser(user.id, course.id, true);
    toast({
      title: 'Course Added',
      description: `${course.name} has been added to your courses.`,
    });
    onImported();

    if (onSelectCourse) {
      const fullCourse = await getCourseById(course.id);
      if (fullCourse) {
        onSelectCourse(fullCourse);
      }
    }

    setSearchQuery('');
    setLocalResults([]);
  };

  return (
    <>
      <CourseSearchStep
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        results={(() => {
          // Dedup: exclude API results that match an already-imported local course
          const localApiIds = new Set(
            localResults.filter(c => c.api_course_id != null).map(c => c.api_course_id)
          );
          return localApiIds.size > 0
            ? searchResults.filter(r => !localApiIds.has(r.id))
            : searchResults;
        })()}
        searching={searching}
        loadingDetails={loadingDetails}
        onSelect={selectSearchResult}
        localResults={localResults}
        onSelectLocal={handleLocalSelect}
      />

      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {step !== 'search' && step !== 'done' ? STEP_TITLES[step] : ''}
            </DialogTitle>
          </DialogHeader>

          {step === 'preview' && selectedCourse && (
            <CoursePreviewStep
              course={selectedCourse}
              onContinue={proceedToMapping}
              onBack={goBack}
            />
          )}

          {(step === 'mapping' || step === 'importing') && (
            <TeeMappingStep
              mappings={teeMappings}
              onUpdateMapping={updateTeeMapping}
              onImport={handleImport}
              onBack={goBack}
              importing={step === 'importing'}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseSearchInline;
